/**
 * ProjectBidWorkspaceHeader
 * --------------------------
 * Project-scoped command card shown at the top of the Bid Requests tab when
 * the user drills in from a Pipeline tile (?project=<id>). Gives a one-glance
 * view of "what's happening with bids on THIS project" so the PM doesn't land
 * on an empty filtered list.
 *
 * Renders:
 *   - Project name + address + stage badge
 *   - 4 stat tiles: scopes, bids received, awaiting reply, reminders due
 *   - Quick actions: + Add scope, send reminders to all awaiting contractors
 *
 * Props:
 *   project           — { id, name, address, stage?, units? }
 *   bidRequests       — bid_request rows belonging to this project
 *   onAddScope?       — opens the "+ New scope on this project" flow
 *   onSendReminders?  — broadcasts a nudge to every awaiting contractor
 *   onClearFilter?    — collapses back to All Projects view
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Building2, MapPin, Plus, Send, Gavel, Mail, Clock, CheckCircle2, X, Loader2, FileSpreadsheet, ArrowRight, Landmark, Upload } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { formatDate } from '@/lib/dateUtils';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ProjectBidWorkspaceHeader({
  project,
  bidRequests,
  user,
  onAddScope,
  onUploadBid,
  onSendReminders,
  onClearFilter,
  readOnly = false,
}) {
  const navigate = useNavigate();
  const [reminding, setReminding] = useState(false);
  const [bidSheet, setBidSheet] = useState(null);
  const [bidSheetLoading, setBidSheetLoading] = useState(false);
  const [creatingSheet, setCreatingSheet] = useState(false);

  const stats = useMemo(() => {
    const total = bidRequests.length;
    const received = bidRequests.reduce((s, b) => s + (b.response_count || 0), 0);
    const awaiting = bidRequests.filter(b => (b.response_count || 0) === 0 && b.status !== 'closed' && b.status !== 'awarded').length;
    const past_due = bidRequests.filter(b => {
      if ((b.response_count || 0) > 0) return false;
      if (!b.due_date) return false;
      try { return new Date(b.due_date) < new Date(); } catch { return false; }
    }).length;
    const awarded = bidRequests.filter(b => b.status === 'awarded').length;
    return { total, received, awaiting, past_due, awarded };
  }, [bidRequests]);

  // Look up an existing bid sheet for this project (if any) so we can render
  // either an "Open Bid Sheet" or "Build Bid Sheet" CTA. We fetch on mount and
  // again whenever the project changes.
  useEffect(() => {
    let cancelled = false;
    if (!project?.id || !user?.id) return;
    setBidSheetLoading(true);
    (async () => {
      try {
        const r = await axios.get(`${API}/bid-sheets`, { params: { user_id: user.id } });
        if (cancelled) return;
        const match = (r.data || []).find(s => s.project_id === project.id);
        setBidSheet(match || null);
      } catch (e) {
        // 429 or transient — non-blocking; the workflow stepper just won't have a deep-link.
      } finally {
        if (!cancelled) setBidSheetLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [project?.id, user?.id]);

  // Workflow step inference. Order: collect → award → submit → funded.
  // Step 1 done when ≥1 bid received OR ≥1 awarded.
  // Step 2 done when a bid sheet exists for this project.
  // Step 3 done when bid sheet has been submitted to bank.
  // Step 4 done when project is funded.
  const isFunded = project?.stage === 'funded' || project?.has_loan;
  const sheetSubmitted = !!(bidSheet?.bank_submission_date) || ['submitted','approved','converted'].includes(bidSheet?.status);
  const sheetApproved = bidSheet?.status === 'approved' || !!bidSheet?.bank_approval_date;
  const step1Done = stats.received > 0 || stats.awarded > 0;
  const step2Done = !!bidSheet;
  const step3Done = sheetSubmitted;
  const step4Done = isFunded || sheetApproved;
  const currentStep = !step1Done ? 1 : !step2Done ? 2 : !step3Done ? 3 : !step4Done ? 4 : 4;

  const stageLabel = project?.stage || (project?.has_loan ? 'funded' : 'collecting');
  const stageTone = {
    collecting: 'bg-amber-100 text-amber-800 border-amber-200',
    sheet_ready: 'bg-blue-100 text-blue-800 border-blue-200',
    submitted: 'bg-violet-100 text-violet-800 border-violet-200',
    funded: 'bg-emerald-100 text-emerald-800 border-emerald-200',
    walked: 'bg-slate-100 text-slate-600 border-slate-200',
  }[stageLabel] || 'bg-amber-100 text-amber-800 border-amber-200';

  const handleSendReminders = async () => {
    if (!onSendReminders || stats.awaiting === 0) return;
    setReminding(true);
    try { await onSendReminders(); } finally { setReminding(false); }
  };

  const openOrCreateBidSheet = async () => {
    if (bidSheet) {
      navigate(`/bid-sheet/${bidSheet.id}`);
      return;
    }
    if (!user?.id || !project?.id) return;
    setCreatingSheet(true);
    try {
      const payload = {
        user_id: user.id,
        project_id: project.id,
        name: project.name || 'Untitled bid sheet',
        property_address: project.address || '',
        imported_from_bids: [],
      };
      const r = await axios.post(`${API}/bid-sheets`, payload);
      const newSheet = r.data;
      setBidSheet(newSheet);
      navigate(`/bid-sheet/${newSheet.id}`);
    } catch (e) {
      // Fall back to the Bid Sheets list so the user can still get there
      navigate('/project-hub?tab=bidsheets');
    } finally {
      setCreatingSheet(false);
    }
  };

  return (
    <div
      className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden mb-5"
      data-testid="project-bid-workspace-header"
    >
      {/* Top strip */}
      <div className="px-5 pt-5 pb-3 flex items-start justify-between gap-3 border-b border-slate-100">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <Building2 className="w-4 h-4 text-slate-500 flex-shrink-0" />
            <h2 className="text-lg font-bold text-slate-900 truncate">
              {project?.name || 'Project'}
            </h2>
            <span
              className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full border ${stageTone}`}
              data-testid="project-bid-stage-badge"
            >
              {stageLabel.replace('_', ' ')}
            </span>
            {project?.units && project.units > 1 ? (
              <span className="text-[10px] font-medium uppercase tracking-wider bg-slate-100 text-slate-700 border border-slate-200 px-2 py-0.5 rounded-full">
                {project.units} units
              </span>
            ) : null}
          </div>
          {project?.address && (
            <div className="text-xs text-slate-500 inline-flex items-center gap-1">
              <MapPin className="w-3 h-3" />
              <span className="truncate">{project.address}</span>
            </div>
          )}
        </div>
        {onClearFilter && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={onClearFilter}
            data-testid="project-bid-clear-filter-btn"
            className="text-xs text-slate-500 hover:text-slate-900 -mr-2"
          >
            <X className="w-3 h-3 mr-1" /> See all projects
          </Button>
        )}
      </div>

      {/* Workflow stepper — shows where this deal is in the bid → bank → fund pipeline */}
      <div className="px-5 py-4 bg-slate-50 border-b border-slate-100" data-testid="deal-workflow-stepper">
        <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-2">
          Deal workflow
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-2">
          <Step n={1} label="Collect bids" sublabel={`${stats.received} of ${stats.total || '?'} in`}        done={step1Done} active={currentStep === 1} />
          <Step n={2} label="Build bid sheet" sublabel={bidSheet ? 'Draft saved' : 'Award & import'}         done={step2Done} active={currentStep === 2} />
          <Step n={3} label="Submit to bank" sublabel={sheetSubmitted ? (sheetApproved ? 'Approved' : 'Awaiting decision') : 'After bid sheet ready'} done={step3Done} active={currentStep === 3} />
          <Step n={4} label="Funded" sublabel={step4Done ? 'Active build' : 'Awaiting funding'}              done={step4Done} active={currentStep === 4 && !step4Done} />
        </div>
      </div>

      {/* Stats row */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 px-5 py-4 bg-slate-50">
        <StatTile label="Scopes"   value={stats.total}    tone="slate"   icon={<Gavel className="w-3.5 h-3.5" />}        testid="stat-scopes" />
        <StatTile label="Bids in"  value={stats.received} tone="emerald" icon={<CheckCircle2 className="w-3.5 h-3.5" />} testid="stat-bids-received" />
        <StatTile label="Awaiting" value={stats.awaiting} tone="amber"   icon={<Clock className="w-3.5 h-3.5" />}        testid="stat-awaiting" />
        <StatTile label="Past due" value={stats.past_due} tone={stats.past_due > 0 ? 'red' : 'slate'} icon={<Mail className="w-3.5 h-3.5" />} testid="stat-past-due" />
      </div>

      {/* Actions row */}
      <div className="px-5 py-3 flex flex-wrap items-center gap-2 border-t border-slate-100">
        {onAddScope && (
          <Button
            type="button"
            size="sm"
            onClick={onAddScope}
            data-testid="project-bid-add-scope-btn"
            className="text-xs"
          >
            <Plus className="w-3.5 h-3.5 mr-1" />
            Add scope to this project
          </Button>
        )}
        {onUploadBid && (
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={onUploadBid}
            data-testid="project-bid-upload-existing-btn"
            className="text-xs"
            title="Got a bid via email or PDF? Upload it here and AI will extract the line items."
          >
            <Upload className="w-3.5 h-3.5 mr-1" />
            Upload bid from email
          </Button>
        )}
        {onSendReminders && stats.awaiting > 0 && (
          <Button
            type="button"
            size="sm"
            variant="outline"
            disabled={reminding}
            onClick={handleSendReminders}
            data-testid="project-bid-remind-all-btn"
            className="text-xs"
          >
            {reminding ? (
              <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />
            ) : (
              <Send className="w-3.5 h-3.5 mr-1" />
            )}
            Nudge {stats.awaiting} awaiting contractor{stats.awaiting === 1 ? '' : 's'}
          </Button>
        )}
        {/* Next-step CTA — promotes the workflow step the user should do next.
            Hidden once the project is funded (workspace is read-only). */}
        {!readOnly && step1Done && !step3Done && (
          <Button
            type="button"
            size="sm"
            variant={step2Done ? 'outline' : 'default'}
            disabled={bidSheetLoading || creatingSheet}
            onClick={openOrCreateBidSheet}
            data-testid="project-bid-open-bidsheet-btn"
            className={`text-xs ${!step2Done ? 'bg-emerald-600 hover:bg-emerald-700 text-white' : ''}`}
          >
            {creatingSheet ? (
              <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />
            ) : (
              <FileSpreadsheet className="w-3.5 h-3.5 mr-1" />
            )}
            {step2Done ? 'Open bid sheet' : 'Build bid sheet'}
            <ArrowRight className="w-3 h-3 ml-1" />
          </Button>
        )}
        {!readOnly && step2Done && !step3Done && bidSheet && (
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() => navigate(`/bid-sheet/${bidSheet.id}?action=submit`)}
            data-testid="project-bid-submit-to-bank-btn"
            className="text-xs"
          >
            <Landmark className="w-3.5 h-3.5 mr-1" />
            Submit to bank
          </Button>
        )}
        {stats.total > 0 && (
          <span className="text-[11px] text-slate-500 ml-auto">
            Last updated {formatDate(new Date())}
          </span>
        )}
      </div>
    </div>
  );
}

function Step({ n, label, sublabel, done, active }) {
  const tone = done
    ? 'border-emerald-300 bg-emerald-50'
    : active
      ? 'border-blue-300 bg-white ring-2 ring-blue-100'
      : 'border-slate-200 bg-white opacity-60';
  const numTone = done
    ? 'bg-emerald-500 text-white'
    : active
      ? 'bg-blue-600 text-white'
      : 'bg-slate-200 text-slate-500';
  return (
    <div className={`flex items-start gap-2 rounded-lg border ${tone} px-3 py-2`} data-testid={`workflow-step-${n}`}>
      <div className={`w-5 h-5 rounded-full ${numTone} text-[11px] font-bold flex items-center justify-center flex-shrink-0`}>
        {done ? <CheckCircle2 className="w-3 h-3" /> : n}
      </div>
      <div className="min-w-0">
        <div className="text-[11px] font-bold uppercase tracking-wider text-slate-700 truncate">{label}</div>
        <div className="text-[10px] text-slate-500 truncate">{sublabel}</div>
      </div>
    </div>
  );
}

function StatTile({ label, value, tone, icon, testid }) {
  const tones = {
    slate: 'bg-white border-slate-200 text-slate-900',
    emerald: 'bg-white border-emerald-200 text-emerald-700',
    amber: 'bg-white border-amber-200 text-amber-700',
    red: 'bg-white border-red-200 text-red-700',
  };
  return (
    <div
      className={`rounded-lg border ${tones[tone] || tones.slate} px-3 py-2`}
      data-testid={testid}
    >
      <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-slate-500">
        {icon}
        {label}
      </div>
      <div className="text-xl font-bold mt-0.5">{value}</div>
    </div>
  );
}
